'use client';

import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { DEFAULT_WHATSAPP_NUMBER, buildGeneralWhatsAppLink } from '@/lib/whatsapp';

interface WhatsAppFloatingButtonProps {
  whatsappNumber?: string;
}

export function WhatsAppFloatingButton({ whatsappNumber }: WhatsAppFloatingButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customMsg, setCustomMsg] = useState('');

  const handleSend = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const link = buildGeneralWhatsAppLink(customMsg, whatsappNumber);
    window.open(link, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
    setCustomMsg('');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 font-sans">
      {/* Floating Popover / Quick Message Modal */}
      {isOpen && (
        <div className="mb-3 w-80 bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
          {/* Header */}
          <div className="bg-[#128C7E] text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-black text-white text-base">
                D
              </div>
              <div>
                <div className="font-bold text-sm">DYRMA Climate Support</div>
                <div className="text-[11px] text-emerald-100 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span>
                  Në linjë në WhatsApp
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white p-1 rounded-lg transition-colors"
              title="Mbyll dritaren"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-4 bg-slate-50 text-xs space-y-3">
            <div className="bg-white p-3 rounded-2xl rounded-tl-sm border border-slate-200 shadow-xs text-slate-700 leading-relaxed">
              Përshëndetje! 👋 Si mund t'ju ndihmojmë sot me pajisjet HVAC, ofertat apo konsulencën teknike?
            </div>

            <form onSubmit={handleSend} className="space-y-2 pt-1">
              <input
                type="text"
                value={customMsg}
                onChange={(e) => setCustomMsg(e.target.value)}
                placeholder="Shkruani mesazhin tuaj..."
                className="w-full px-3 py-2 text-xs rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#25D366] bg-white text-slate-800"
              />
              <button
                type="submit"
                className="w-full py-2.5 px-4 rounded-xl bg-[#25D366] hover:bg-[#20ba59] text-white font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition-colors"
              >
                <Send className="w-3.5 h-3.5" />
                Dërgo në WhatsApp
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Main Floating Bubble Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20ba59] text-white shadow-xl hover:shadow-2xl flex items-center justify-center transition-all hover:scale-105 group"
        title="Bisedo në WhatsApp me DYRMA Climate"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91C2.13 13.66 2.59 15.36 3.45 16.86L2.05 22L7.3 20.62C8.75 21.41 10.38 21.83 12.04 21.83C17.5 21.83 21.95 17.38 21.95 11.92C21.95 9.27 20.92 6.78 19.05 4.91C17.18 3.03 14.69 2 12.04 2ZM12.05 20.15C10.56 20.15 9.1 19.75 7.83 19L7.53 18.82L4.41 19.64L5.24 16.59L5.04 16.27C4.22 14.97 3.79 13.46 3.79 11.91C3.79 7.37 7.5 3.66 12.05 3.66C14.25 3.66 16.32 4.52 17.88 6.07C19.43 7.63 20.29 9.7 20.29 11.91C20.28 16.46 16.58 20.15 12.05 20.15ZM16.57 14.39C16.32 14.26 15.1 13.66 14.87 13.58C14.64 13.5 14.48 13.46 14.31 13.71C14.15 13.96 13.68 14.51 13.54 14.67C13.4 14.84 13.26 14.86 13.01 14.73C12.76 14.61 11.96 14.34 11.01 13.49C10.27 12.83 9.77 12.01 9.63 11.76C9.48 11.51 9.61 11.38 9.74 11.26C9.85 11.14 9.99 10.96 10.12 10.82C10.24 10.67 10.28 10.57 10.37 10.4C10.45 10.24 10.41 10.09 10.35 9.97C10.28 9.85 9.79 8.64 9.58 8.14C9.38 7.66 9.18 7.72 9.03 7.71C8.89 7.71 8.72 7.7 8.56 7.7C8.39 7.7 8.12 7.76 7.89 8.01C7.66 8.26 7.02 8.86 7.02 10.08C7.02 11.3 7.91 12.47 8.03 12.64C8.16 12.8 9.77 15.29 12.24 16.36C12.83 16.61 13.29 16.76 13.64 16.87C14.23 17.06 14.77 17.03 15.2 16.97C15.68 16.9 16.67 16.37 16.88 15.79C17.08 15.2 17.08 14.7 17.02 14.59C16.96 14.49 16.82 14.42 16.57 14.39Z" />
        </svg>
      </button>
    </div>
  );
}
